// Importation correcte pour résoudre le problème d'autoTable
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';

/**
 * Génère un PDF à partir d'une facture avec un design professionnel amélioré
 * @param {Object} invoiceData - Données de la facture
 * @param {Object} memberData - Données du membre associé
 * @returns {jsPDF} Document PDF
 */
export const generateInvoicePDF = (invoiceData, memberData) => {
  // Couleurs et styles
  const colors = {
    primary: '#3730A3',      // Indigo - couleur principale
    secondary: '#8B5CF6',    // Violet - couleur secondaire
    accent: '#10B981',       // Vert - couleur d'accent
    dark: '#1F2937',         // Gris foncé - texte principal
    light: '#F9FAFB',        // Blanc cassé - fond
    muted: '#6B7280',        // Gris moyen - texte secondaire
    success: '#059669',      // Vert - textes positifs
    danger: '#DC2626',       // Rouge - textes négatifs
    warning: '#F59E0B',      // Orange - textes d'avertissement
  };

  // Création du document PDF
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true
  });
  
  // Dimensions de la page
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  
  // Ajout d'un fond élégant
  doc.setFillColor(colors.light);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
  
  // Bande de couleur en haut de la page
  doc.setFillColor(colors.primary);
  doc.rect(0, 0, pageWidth, 15, 'F');
  
  // Bande de couleur en bas de la page
  doc.setFillColor(colors.primary);
  doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
  
  // Effet de dégradé subtil
  for (let i = 0; i < 20; i++) {
    const alpha = 0.1 - (i * 0.005);
    if (alpha > 0) {
      doc.setFillColor(0, 0, 0);
      doc.setGState(new doc.GState({opacity: alpha}));
      doc.rect(0, 15 + i, pageWidth, 1, 'F');
      doc.rect(0, pageHeight - 15 - i, pageWidth, 1, 'F');
    }
  }
  
  doc.setGState(new doc.GState({opacity: 1.0}));
  
  // Filigrane discret
  doc.setTextColor(colors.primary);
  doc.setGState(new doc.GState({opacity: 0.03}));
  doc.setFontSize(120);
  doc.setFont('helvetica', 'bold');
  doc.text('GYMIT', pageWidth / 2, pageHeight / 2, {
    align: 'center',
    angle: 45
  });
  doc.setGState(new doc.GState({opacity: 1.0}));
  
  // --- EN-TÊTE DE LA FACTURE ---
  const headerY = 30;
  
  // Logo ou nom stylisé
  doc.setFillColor(colors.primary);
  doc.roundedRect(margin, headerY, 50, 15, 3, 3, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('GYMIT', margin + 25, headerY + 9, {align: 'center'});
  
  // Texte FACTURE
  doc.setFillColor(colors.primary);
  doc.setTextColor(colors.dark);
  doc.setFontSize(32);
  doc.setFont('helvetica', 'bold');
  doc.text('FACTURE', pageWidth - margin, headerY + 5, {align: 'right'});
  
  // Ligne de séparation élégante
  doc.setDrawColor(colors.primary);
  doc.setLineWidth(0.5);
  doc.line(margin, headerY + 20, pageWidth - margin, headerY + 20);
  
  // Numéro et date de la facture
  const infoY = headerY + 30;
  doc.setTextColor(colors.dark);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(`Référence: ${invoiceData.invoiceNumber}`, margin, infoY);
  
  doc.setTextColor(colors.muted);
  doc.setFontSize(10);
  doc.text(`Date: ${new Date(invoiceData.issueDate).toLocaleDateString('fr-FR')}`, margin, infoY + 7);
  
  if (invoiceData.dueDate) {
    doc.text(`Échéance: ${new Date(invoiceData.dueDate).toLocaleDateString('fr-FR')}`, margin, infoY + 14);
  }
  
  // --- INFORMATIONS CLIENT ET ENTREPRISE ---
  const partiesY = infoY + 25;
  
  // Coordonnées de l'entreprise
  doc.setFillColor(colors.light);
  doc.setDrawColor(colors.primary);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin, partiesY, contentWidth * 0.45, 40, 2, 2, 'FD');
  
  doc.setFillColor(colors.primary);
  doc.rect(margin, partiesY, contentWidth * 0.45, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('DE', margin + 5, partiesY + 5.5);
  
  doc.setTextColor(colors.dark);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('SYSTÈME DE GESTION SALLE DE MUSCULATION', margin + 5, partiesY + 15);
  
  doc.setTextColor(colors.muted);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('contact@gymit.dz', margin + 5, partiesY + 22);
  doc.text('+213 XX XX XX XX', margin + 5, partiesY + 28);
  doc.text('123, Boulevard Mohammed V', margin + 5, partiesY + 34);
  
  // Coordonnées du client
  const clientBoxX = margin + contentWidth * 0.55;
  doc.setFillColor(colors.light);
  doc.setDrawColor(colors.primary);
  doc.roundedRect(clientBoxX, partiesY, contentWidth * 0.45, 40, 2, 2, 'FD');
  
  doc.setFillColor(colors.primary);
  doc.rect(clientBoxX, partiesY, contentWidth * 0.45, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('FACTURÉ À', clientBoxX + 5, partiesY + 5.5);
  
  if (memberData) {
    doc.setTextColor(colors.dark);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`${memberData.firstName} ${memberData.lastName}`, clientBoxX + 5, partiesY + 15);
    
    doc.setTextColor(colors.muted);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(memberData.email || '', clientBoxX + 5, partiesY + 22);
    doc.text(memberData.phone || '', clientBoxX + 5, partiesY + 28);
    doc.text(`Code membre: ${memberData.memberCode || ''}`, clientBoxX + 5, partiesY + 34);
  }
  
  // --- TABLEAU DES ARTICLES ---
  const tableStartY = partiesY + 50;
  
  // Style pour l'en-tête du tableau
  const tableHeadStyle = {
    fillColor: colors.primary,
    textColor: [255, 255, 255],
    fontStyle: 'bold',
    halign: 'center',
    valign: 'middle',
    fontSize: 10,
    cellPadding: {top: 5, bottom: 5, left: 3, right: 3},
    lineColor: [200, 200, 200]
  };
  
  const tableBodyStyle = {
    textColor: [50, 50, 50],
    fontSize: 9,
    cellPadding: {top: 4, bottom: 4, left: 3, right: 3},
    lineColor: [200, 200, 200]
  };
  
  const tableColumns = [
    { 
      header: 'Description', 
      dataKey: 'description'
    },
    { 
      header: 'Quantité', 
      dataKey: 'quantity'
    },
    { 
      header: 'Prix unitaire', 
      dataKey: 'price'
    },
    { 
      header: 'Total', 
      dataKey: 'total'
    }
  ];

  const tableRows = invoiceData.items.map(item => {
    return {
      description: item.description,
      quantity: item.quantity.toString(),
      price: `${item.price.toLocaleString('fr-FR')} DA`,
      total: `${item.total.toLocaleString('fr-FR')} DA`
    };
  });

  // Ajouter le tableau avec un design amélioré
  autoTable(doc, {
    startY: tableStartY,
    columns: tableColumns,
    body: tableRows,
    headStyles: tableHeadStyle,
    bodyStyles: tableBodyStyle,
    alternateRowStyles: {
      fillColor: [249, 250, 251]
    },
    columnStyles: {
      0: { cellWidth: 80 },
      1: { halign: 'center' },
      2: { halign: 'right' },
      3: { halign: 'right' }
    },
    margin: { left: margin, right: margin },
    tableLineColor: [200, 200, 200],
    tableLineWidth: 0.1,
    styles: {
      font: 'helvetica',
      overflow: 'ellipsize',
      cellWidth: 'wrap'
    },
    didDrawCell: (data) => {
      // Ajouter une ombre légère à l'en-tête du tableau
      if (data.section === 'head' && data.row.index === 0) {
        const { x, y, width, height } = data.cell;
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.1);
        doc.line(x, y + height, x + width, y + height);
      }
    }
  });

  // Récupérer la position Y finale du tableau
  const finalY = (doc.lastAutoTable ? doc.lastAutoTable.finalY : tableStartY + 40) + 10;
  
  // --- TOTAUX ET CALCULS ---
  const totalsStartX = pageWidth - margin - 80;
  const totalsWidth = 80;
  let currentTotalsY = finalY + 5;
  
  // Sous-total
  doc.setFillColor(246, 248, 250);
  doc.setDrawColor(230, 230, 230);
  doc.rect(totalsStartX, currentTotalsY, totalsWidth, 10, 'FD');
  
  doc.setTextColor(colors.muted);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Sous-total:', totalsStartX - 35, currentTotalsY + 6.5);
  
  doc.setTextColor(colors.dark);
  doc.text(`${invoiceData.subtotal.toLocaleString('fr-FR')} DA`, totalsStartX + totalsWidth - 5, currentTotalsY + 6.5, { align: 'right' });
  
  currentTotalsY += 12;
  
  // Remise
  if (invoiceData.discount > 0) {
    doc.setFillColor(246, 248, 250);
    doc.setDrawColor(230, 230, 230);
    doc.rect(totalsStartX, currentTotalsY, totalsWidth, 10, 'FD');
    
    doc.setTextColor(colors.muted);
    doc.text('Remise:', totalsStartX - 35, currentTotalsY + 6.5);
    
    doc.setTextColor(colors.danger);
    doc.text(`-${invoiceData.discount.toLocaleString('fr-FR')} DA`, totalsStartX + totalsWidth - 5, currentTotalsY + 6.5, { align: 'right' });
    
    currentTotalsY += 12;
  }
  
  // Taxes
  if (invoiceData.tax > 0) {
    doc.setFillColor(246, 248, 250);
    doc.setDrawColor(230, 230, 230);
    doc.rect(totalsStartX, currentTotalsY, totalsWidth, 10, 'FD');
    
    doc.setTextColor(colors.muted);
    doc.text('Taxes:', totalsStartX - 35, currentTotalsY + 6.5);
    
    doc.setTextColor(colors.dark);
    doc.text(`+${invoiceData.tax.toLocaleString('fr-FR')} DA`, totalsStartX + totalsWidth - 5, currentTotalsY + 6.5, { align: 'right' });
    
    currentTotalsY += 12;
  }
  
  // Total final avec design spécial
  doc.setFillColor(colors.primary);
  doc.roundedRect(totalsStartX - 40, currentTotalsY, totalsWidth + 40, 15, 2, 2, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL:', totalsStartX - 35, currentTotalsY + 10);
  doc.setFontSize(11);
  doc.text(`${invoiceData.total.toLocaleString('fr-FR')} DA`, totalsStartX + totalsWidth - 5, currentTotalsY + 10, { align: 'right' });
  
  // --- NOTES ET CONDITIONS ---
  const notesY = Math.max(currentTotalsY + 25, finalY + 50);
  
  // Cadre élégant pour les notes et conditions
  doc.setFillColor(250, 250, 250);
  doc.setDrawColor(230, 230, 230);
  doc.roundedRect(margin, notesY, contentWidth, 40, 2, 2, 'FD');
  
  if (invoiceData.notes) {
    doc.setTextColor(colors.dark);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Notes:', margin + 5, notesY + 8);
    
    doc.setTextColor(colors.muted);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    
    const notesLines = doc.splitTextToSize(invoiceData.notes, contentWidth - 10);
    doc.text(notesLines, margin + 5, notesY + 15);
  }
  
  if (invoiceData.terms) {
    const termsY = invoiceData.notes ? notesY + 30 : notesY + 8;
    
    doc.setTextColor(colors.dark);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Conditions de paiement:', margin + 5, termsY);
    
    doc.setTextColor(colors.muted);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    
    const termsLines = doc.splitTextToSize(invoiceData.terms, contentWidth - 10);
    doc.text(termsLines, margin + 5, termsY + 7);
  }
  
  // --- PIED DE PAGE ---
  const footerY = pageHeight - 25;
  
  // Ligne de séparation
  doc.setDrawColor(colors.primary);
  doc.setLineWidth(0.5);
  doc.line(margin, footerY, pageWidth - margin, footerY);
  
  // Informations légales et coordonnées
  doc.setTextColor(colors.muted);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  
  doc.text('SYSTÈME DE GESTION SALLE DE MUSCULATION', pageWidth / 2, footerY + 5, { align: 'center' });
  doc.text('Merci pour votre confiance !', pageWidth / 2, footerY + 10, { align: 'center' });
  
  // QR Code (simulé par un carré pour l'instant)
  doc.setFillColor(colors.muted);
  doc.roundedRect(margin, footerY - 15, 15, 15, 1, 1, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(5);
  doc.text('QR', margin + 7.5, footerY - 7.5, { align: 'center' });
  
  // Numéro de page
  doc.setTextColor(colors.muted);
  doc.setFontSize(8);
  const pageInfo = `Page ${doc.getCurrentPageInfo().pageNumber} / ${doc.getNumberOfPages()}`;
  doc.text(pageInfo, pageWidth - margin, footerY + 10, { align: 'right' });
  
  // Appliquer la même mise en page à toutes les pages
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    
    // Bandes de couleur en haut et en bas de chaque page
    doc.setFillColor(colors.primary);
    doc.rect(0, 0, pageWidth, 15, 'F');
    doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
    
    // Effet de dégradé
    for (let j = 0; j < 20; j++) {
      const alpha = 0.1 - (j * 0.005);
      if (alpha > 0) {
        doc.setFillColor(0, 0, 0);
        doc.setGState(new doc.GState({opacity: alpha}));
        doc.rect(0, 15 + j, pageWidth, 1, 'F');
        doc.rect(0, pageHeight - 15 - j, pageWidth, 1, 'F');
      }
    }
    
    doc.setGState(new doc.GState({opacity: 1.0}));
    
    // Pied de page sur chaque page
    if (i > 1) {
      doc.setDrawColor(colors.primary);
      doc.setLineWidth(0.5);
      doc.line(margin, footerY, pageWidth - margin, footerY);
      
      doc.setTextColor(colors.muted);
      doc.setFontSize(8);
      doc.text(`Page ${i} / ${totalPages}`, pageWidth - margin, footerY + 10, { align: 'right' });
      doc.text('SYSTÈME DE GESTION SALLE DE MUSCULATION', pageWidth / 2, footerY + 5, { align: 'center' });
      doc.text('Merci pour votre confiance !', pageWidth / 2, footerY + 10, { align: 'center' });
    }
  }

  return doc;
};

/**
 * Exporte une facture au format PDF à partir des données
 * @param {Object} invoiceData - Données de la facture
 * @param {Object} memberData - Données du membre associé
 */
export const downloadInvoicePDF = (invoiceData, memberData) => {
  const doc = generateInvoicePDF(invoiceData, memberData);
  doc.save(`Facture_${invoiceData.invoiceNumber}.pdf`);
};

/**
 * Génère un PDF à partir d'un élément HTML
 * @param {HTMLElement} element - Élément HTML à convertir
 * @param {string} filename - Nom du fichier PDF
 * @param {Function} callback - Fonction de rappel après génération
 */
export const generatePDFFromHTML = async (element, filename, callback = null) => {
  const canvas = await html2canvas(element, {
    scale: 2,
    logging: false,
    useCORS: true
  });

  const imgData = canvas.toDataURL('image/jpeg', 0.95);
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const imgWidth = 210; // A4 width in mm
  const pageHeight = 297; // A4 height in mm
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  let heightLeft = imgHeight;
  let position = 0;
  let pageData = imgData;

  pdf.addImage(pageData, 'JPEG', 0, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;

  while (heightLeft >= 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(pageData, 'JPEG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }

  if (callback && typeof callback === 'function') {
    callback(pdf);
  } else {
    pdf.save(filename);
  }
}; 